import * as Sentry from '@sentry/react';
import {
	Elements,
	useStripe,
	useElements,
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import './Stripe.css';
import { useTranslation } from '../../../i18n/i18n';
import { CheckoutContainer } from '../../../state/checkout';
import { useConfig } from '../../../config';
import { useCallback, useEffect, useState } from 'react';
import { InputAlertMessage } from '../../../components/Form/InputAlertMessage';
import { Stripe } from '@stripe/stripe-js';
import { useFormValidationContainer } from '../../Form/FormValidatorContainer';

type StripeLoadStatus = 'loading' | 'ready' | 'error';

export function useStripeInstance() {
	const config = useConfig();
	const [stripe, setStripe] = useState<Stripe | null>(null);
	const [status, setStatus] = useState<StripeLoadStatus>('loading');

	useEffect(() => {
		let active = true;
		setStatus('loading');
		setStripe(null);

		const stripePromise = loadStripe(config.stripePubKey);
		stripePromise
			.then((res) => {
				if (!active) return;

				if (!res) {
					setStatus('error');
					Sentry.captureMessage('stripe_init_failed', {
						level: 'error',
						tags: {
							payment_provider: 'stripe',
							phase: 'init',
						},
					});
					return;
				}

				setStripe(res as Stripe);
				setStatus('ready');
			})
			.catch((error) => {
				if (!active) return;
				setStatus('error');
				Sentry.withScope((scope) => {
					scope.setTag('payment_provider', 'stripe');
					scope.setTag('phase', 'init');
					Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
				});
			});

		return () => {
			active = false;
		};
	}, [config.stripePubKey]);

	return { stripe, status };
}

export const StripeForm = (): JSX.Element => {
	const { stripe: stripeInstance, status } = useStripeInstance();
	const fallbackMessage = 'Card payment is temporarily unavailable. Please try Klarna or PayPal.';

	return (
		<>
			{status === 'error' && <InputAlertMessage message={fallbackMessage} type="error" />}
			<Elements stripe={stripeInstance}>
				<Form stripeLoadStatus={status} />
			</Elements>
		</>
	);
};

function Form({ stripeLoadStatus }: { stripeLoadStatus: StripeLoadStatus }) {
	const t = useTranslation().checkout;
	const stripe = useStripe();
	const elements = useElements();
	const { setStripePaymentMethodCreator, setStripeToken } = CheckoutContainer.useContainer();
	const [error, setError] = useState('');
	const [formIsPrestine, setFormIsPrestine] = useState(true);
	const formValidation = useFormValidationContainer();

	const createPaymentMethod = useCallback(async () => {
		if (stripeLoadStatus === 'error') {
			const initError = new Error('Stripe is unavailable');
			Sentry.withScope((scope) => {
				scope.setTag('payment_provider', 'stripe');
				scope.setTag('phase', 'createPaymentMethod');
				Sentry.captureException(initError);
			});
			setError('Card payment is temporarily unavailable. Please try Klarna or PayPal.');
			setStripeToken(null);
			return null;
		}

		if (!stripe || !elements) {
			setStripeToken(null);
			return null;
		}

		const cardEl = elements.getElement(CardNumberElement);
		if (!cardEl) {
			setStripeToken(null);
			return null;
		}

		const res = await stripe.createPaymentMethod({
			type: 'card',
			card: cardEl,
		});
		if (res.error || !res.paymentMethod?.id) {
			Sentry.withScope((scope) => {
				scope.setTag('payment_provider', 'stripe');
				scope.setTag('phase', 'createPaymentMethod');
				scope.setExtra('has_payment_method', !!res.paymentMethod?.id);
				Sentry.captureMessage('stripe_create_payment_method_failed', {
					level: 'error',
				});
			});
			setError(res.error?.message ?? '');
			setStripeToken(null);
			return null;
		}

		setError('');
		setStripeToken(res.paymentMethod.id);
		return res.paymentMethod.id;
	}, [elements, setStripeToken, stripe, stripeLoadStatus]);

	useEffect(() => {
		setStripePaymentMethodCreator(createPaymentMethod);

		return () => {
			setStripePaymentMethodCreator(null);
		};
	}, [createPaymentMethod, setStripePaymentMethodCreator]);

	function handleOnChange() {
		setFormIsPrestine(false);
		setError('');
		setStripeToken(null);
	}

	return (
		<div className="stripe-container">
			<CardNumberElement
				options={{
					placeholder: t.cardNumber,
				}}
				className="stripe-element"
				onChange={handleOnChange}
			/>

			<div className="stripe-container-footer">
				<CardExpiryElement
					className="stripe-element"
					onChange={handleOnChange}
					options={{ placeholder: t.cardExpiry }}
				/>
				<CardCvcElement
					className="stripe-element"
					onChange={handleOnChange}
					options={{ placeholder: t.cvcCode }}
				/>
			</div>
			{!formValidation.paymentCard.isValid && formIsPrestine && (
				<InputAlertMessage message={formValidation.paymentCard.message} type="error" />
			)}
			{error && <InputAlertMessage message={error} type="error" />}
		</div>
	);
}
